import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import { DETAIL_TENANT_PARAM } from "./detailRoutes";

/** A positive integer tenant id, or null for anything else (absent, empty, garbage). */
export function parseTenantParam(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
}

/**
 * The tenant a detail view reads its resource in: `?tenantId=` when the URL carries it,
 * otherwise the SideNav selection — the same fallback these views always had.
 *
 * A partner looking at "all customers" opens a child's resource from the list; the list
 * used to hand that child's tenant over in `location.state.tenantOverride`, which a reload
 * or a pasted link loses (CTX-773). The URL now carries it instead.
 *
 * Nothing here trusts the value: the backend's `getTenantId` only honours a `tenantId` the
 * caller may see (a partner's own children), and falls back to the caller's tenant
 * otherwise.
 */
export function useDetailTenantId(): number {
  const [searchParams] = useSearchParams();
  const selectedTenantId = useAppSelector((state) => state.selection.tenantId);
  return parseTenantParam(searchParams.get(DETAIL_TENANT_PARAM)) ?? selectedTenantId;
}
